/**
 * AnalyticsFilterBar — Compact filter chips for dashboard (target: <48px height)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from './ui/badge';
import {
  Filter, Tag, UserCheck, Activity, X, ChevronDown
} from 'lucide-react';
import api from '../utils/api';


const SOURCE_KEYS = [
  { key: 'META_ADS', labelKey: 'provider.analyticsFilter.sources.metaAds' },
  { key: 'GOOGLE_ADS', labelKey: 'provider.analyticsFilter.sources.googleAds' },
  { key: 'ORGANIC', labelKey: 'provider.analyticsFilter.sources.organic' },
  { key: 'WHATSAPP', labelKey: 'provider.analyticsFilter.sources.whatsapp' },
  { key: 'INSTAGRAM', labelKey: 'provider.analyticsFilter.sources.instagram' },
  { key: 'COORDINATOR_REFERRAL', labelKey: 'provider.analyticsFilter.sources.referral' },
  { key: 'OTHER', labelKey: 'provider.analyticsFilter.sources.other' },
];

const STATUS_KEYS = [
  { key: 'NEW', labelKey: 'provider.analyticsFilter.statuses.new' },
  { key: 'CONTACTED', labelKey: 'provider.analyticsFilter.statuses.contacted' },
  { key: 'QUALIFIED', labelKey: 'provider.analyticsFilter.statuses.qualified' },
  { key: 'OFFER_SENT', labelKey: 'provider.analyticsFilter.statuses.offerSent' },
  { key: 'NEGOTIATION', labelKey: 'provider.analyticsFilter.statuses.negotiation' },
  { key: 'WON', labelKey: 'provider.analyticsFilter.statuses.won' },
  { key: 'LOST', labelKey: 'provider.analyticsFilter.statuses.lost' },
];

const ChipDropdown = ({ label, icon: Icon, options, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.key === value);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-all ${
          value
            ? 'bg-slate-800 text-white border border-slate-800 shadow-sm'
            : 'bg-slate-50 text-slate-500 border border-slate-200 hover:border-slate-300 hover:bg-slate-100'
        }`}
        data-testid={testId}
      >
        <Icon className="h-2.5 w-2.5" />
        {selected?.label || label}
        {value ? (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(null); setOpen(false); }}
            className="ml-0.5 hover:bg-white/20 rounded-full"
            data-testid={`${testId}-clear`}
          >
            <X className="h-2.5 w-2.5" />
          </span>
        ) : (
          <ChevronDown className="h-2.5 w-2.5" />
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-0.5 min-w-[140px] z-30" data-testid={`${testId}-menu`}>
          {options.map(o => (
            <button
              key={o.key}
              onClick={() => { onChange(o.key); setOpen(false); }}
              className={`w-full px-2.5 py-1 text-left text-[11px] hover:bg-slate-50 ${
                o.key === value ? 'bg-slate-100 font-semibold' : 'text-slate-700'
              }`}
              data-testid={`${testId}-option-${o.key}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AnalyticsFilterBar = ({ clinicId, filters, onChange }) => {
  const { t } = useTranslation();
  const [assignees, setAssignees] = useState([]);

  const SOURCES = SOURCE_KEYS.map(s => ({ key: s.key, label: t(s.labelKey) }));
  const STATUSES = STATUS_KEYS.map(s => ({ key: s.key, label: t(s.labelKey) }));

  const fetchAssignees = useCallback(async () => {
    if (!clinicId) return;
    try {
      const res = await api.get(`/v2/analytics/assignees?clinic_id=${clinicId}`);
      {
        const data = res.data;
        setAssignees((data.assignees || []).map(a => ({ key: a.user_id, label: a.user_id })));
      }
    } catch (err) {
      console.error('Fetch assignees error:', err);
    }
  }, [clinicId]);

  useEffect(() => { fetchAssignees(); }, [fetchAssignees]);

  const activeCount = [filters.source, filters.assigned_to, filters.status].filter(Boolean).length;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="analytics-filter-bar">
      <div className="flex items-center gap-1 text-[11px] text-slate-400 font-medium">
        <Filter className="h-3 w-3" />
        {activeCount > 0 && (
          <Badge className="bg-sky-600 text-white text-[9px] h-3.5 px-1 leading-none">{activeCount}</Badge>
        )}
      </div>

      <ChipDropdown
        label={t('provider.analyticsFilter.source')}
        icon={Tag}
        options={SOURCES}
        value={filters.source}
        onChange={(v) => onChange({ ...filters, source: v })}
        testId="filter-source"
      />

      {assignees.length > 0 && (
        <ChipDropdown
          label={t('provider.analyticsFilter.operations')}
          icon={UserCheck}
          options={assignees}
          value={filters.assigned_to}
          onChange={(v) => onChange({ ...filters, assigned_to: v })}
          testId="filter-coordinator"
        />
      )}

      <ChipDropdown
        label={t('provider.analyticsFilter.status')}
        icon={Activity}
        options={STATUSES}
        value={filters.status}
        onChange={(v) => onChange({ ...filters, status: v })}
        testId="filter-status"
      />

      {activeCount > 0 && (
        <button
          onClick={() => onChange({ source: null, assigned_to: null, status: null })}
          className="text-[10px] text-slate-400 hover:text-slate-600 underline"
          data-testid="filter-clear-all"
        >
          {t('provider.analyticsFilter.clear')}
        </button>
      )}
    </div>
  );
};

export default AnalyticsFilterBar;
